import { Injectable } from "@angular/core";
import { PendingOrder } from "src/services/pending-order/pending-order.interface";
import { ApiService } from "../http/api.service";
import { apiPath } from "../../shared/api-path.const";
import { PendingOrderResponse, CreatePendingOrderRequest } from "../../shared/backend-api.types";
import { parseFulfillmentDateTime } from "../utils/date.utils";
import { OrderAddress, Preferences } from "../order/order.interface";

interface PendingOrderAddressJson {
  apartment?: string | null;
  country?: string | null;
  formattedAddress?: string | null;
  lat?: string | null;
  lng?: string | null;
  name?: string | null;
  placeId?: string | null;
  state?: string | null;
  street?: string | null;
  zipCode?: string | null;
}

interface PendingOrderPreferencesJson {
  accommodation?: Preferences["accommodation"] | null;
  details?: string | null;
  hotelName?: string | null;
  instructions?: Preferences["instruction"] | null;
  reservationDetails?: string | null;
  unitNumber?: string | null;
}

// Fulfillment windows are Mountain Time business hours; deriving the calendar date and
// wall-clock times in America/Denver (instead of slicing the UTC timestamp / using the
// browser timezone) keeps legacy evening windows from shifting to the next day.
function toDenverDateTimeParts(isoTimestamp: string): { date: string; hour: number; minute: string } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Denver",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(isoTimestamp));
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((part) => part.type === type)?.value ?? "";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, hour: Number(get("hour")), minute: get("minute") };
}

function extractDateAndTimeFromFulfillment(
  earliestFulfillment: string | null,
  latestFulfillment: string | null,
): { fulfillmentDate: string; fulfillmentTime: string } {
  if (!earliestFulfillment || !latestFulfillment) {
    return { fulfillmentDate: "", fulfillmentTime: "" };
  }
  // One malformed timestamp must not break the whole pending-orders load: Intl throws a
  // RangeError on invalid dates, so degrade that row to empty date fields instead.
  if (isNaN(new Date(earliestFulfillment).getTime()) || isNaN(new Date(latestFulfillment).getTime())) {
    return { fulfillmentDate: "", fulfillmentTime: "" };
  }
  const earliest = toDenverDateTimeParts(earliestFulfillment);
  const latest = toDenverDateTimeParts(latestFulfillment);
  const formatTime = (time: { hour: number; minute: string }) => `${time.hour}:${time.minute}`;
  return {
    fulfillmentDate: earliest.date,
    fulfillmentTime: `${formatTime(earliest)} - ${formatTime(latest)}`,
  };
}

function toOrderAddress(addressJson: PendingOrderAddressJson | null): OrderAddress | undefined {
  if (!addressJson) {
    return undefined;
  }
  return {
    apartment: addressJson.apartment ?? undefined,
    country: addressJson.country ?? "",
    formatted_address: addressJson.formattedAddress ?? "",
    lat: addressJson.lat ?? "",
    lng: addressJson.lng ?? "",
    name: addressJson.name ?? undefined,
    placeId: addressJson.placeId ?? "",
    state: addressJson.state ?? "",
    street: addressJson.street ?? "",
    zipCode: addressJson.zipCode ?? "",
  };
}

function toPreferences(preferencesJson: PendingOrderPreferencesJson | null): Preferences | undefined {
  if (!preferencesJson) {
    return undefined;
  }
  return {
    accommodation: preferencesJson.accommodation ?? undefined,
    details: preferencesJson.details ?? undefined,
    hotelName: preferencesJson.hotelName ?? undefined,
    instruction: preferencesJson.instructions ?? undefined,
    reservationDetails: preferencesJson.reservationDetails ?? undefined,
    unitNumber: preferencesJson.unitNumber ?? undefined,
  };
}

function toAddressJson(address: OrderAddress): Record<string, unknown> {
  return {
    apartment: address.apartment,
    country: address.country,
    formattedAddress: address.formatted_address,
    lat: address.lat !== undefined && address.lat !== null ? String(address.lat) : undefined,
    lng: address.lng !== undefined && address.lng !== null ? String(address.lng) : undefined,
    name: address.name,
    placeId: address.placeId,
    state: address.state,
    street: address.street,
    zipCode: address.zipCode,
  };
}

function toPreferencesJson(preferences: Preferences): Record<string, unknown> {
  return {
    accommodation: preferences.accommodation ?? undefined,
    details: preferences.details ?? undefined,
    hotelName: preferences.hotelName ?? undefined,
    instructions: preferences.instruction ?? undefined,
    reservationDetails: preferences.reservationDetails ?? undefined,
    unitNumber: preferences.unitNumber ?? undefined,
  };
}

function toPendingOrder(response: PendingOrderResponse): PendingOrder {
  const { fulfillmentDate, fulfillmentTime } = extractDateAndTimeFromFulfillment(
    response.earliestFulfillment,
    response.latestFulfillment,
  );
  return {
    id: response.pendingOrderId,
    baseLocation: response.baseLocation,
    createdAt: response.createdAt,
    serviceType: response.serviceType,
    fulfillmentDate,
    fulfillmentTime,
    items: response.itemIds,
    address: toOrderAddress(response.addressJson as PendingOrderAddressJson | null),
    preferences: toPreferences(response.preferencesJson as PendingOrderPreferencesJson | null),
    userId: response.userId,
  } as unknown as PendingOrder;
}

@Injectable({
  providedIn: "root",
})
export class PendingOrderApiService {
  constructor(private readonly apiService: ApiService) {}

  public async getById(id: string): Promise<PendingOrder> {
    const response = await this.apiService.sendGetRequest<PendingOrderResponse>(
      `${apiPath.pendingOrder}/${id}`,
      undefined,
    );
    return toPendingOrder(response);
  }

  public async getAll(): Promise<PendingOrder[]> {
    const response = await this.apiService.sendGetRequest<PendingOrderResponse[]>(apiPath.pendingOrder, undefined);
    return response.map(toPendingOrder);
  }

  public async create(order: PendingOrder): Promise<string> {
    const { earliest, latest } = parseFulfillmentDateTime(order.fulfillmentDate, order.fulfillmentTime);
    const body: CreatePendingOrderRequest = {
      baseLocation: order.baseLocation,
      serviceType: order.serviceType as unknown as CreatePendingOrderRequest["serviceType"],
      earliestFulfillment: earliest,
      latestFulfillment: latest,
      itemIds: order.items,
      addressJson: order.address ? toAddressJson(order.address) : undefined,
      preferencesJson: order.preferences ? toPreferencesJson(order.preferences) : undefined,
    };
    const response = await this.apiService.sendPostRequest<CreatePendingOrderRequest, PendingOrderResponse>(
      apiPath.pendingOrder,
      body,
    );
    return response.pendingOrderId;
  }

  public async delete(id: string): Promise<void> {
    await this.apiService.sendDeleteRequest<void>(`${apiPath.pendingOrder}/${id}`, undefined);
  }

  public async promote(id: string): Promise<void> {
    await this.apiService.sendPostRequest<undefined, void>(`${apiPath.pendingOrder}/${id}/promote`, undefined);
  }
}
